import { useMemo, useState } from "react";
import { CupSoda, Sparkles, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const BASES = ["Coca-Cola", "Dr Pepper", "Sprite", "Mountain Dew", "Root Beer", "Diet Coke", "Lemonade"];
const CREAMERS = ["None", "Coconut Cream", "Half & Half", "Vanilla Sweet Cream"];
const SYRUPS = ["Raspberry", "Peach", "Mango", "Cherry", "Vanilla", "Lime", "Blue Raspberry", "Watermelon"];
const ADDINS = ["Chewy Ice", "Fresh Lime Wedge", "Popping Boba", "Whipped Top"];

export function SodaBuilder() {
  const [base, setBase] = useState("Coca-Cola");
  const [creamer, setCreamer] = useState("Coconut Cream");
  const [syrups, setSyrups] = useState(["Raspberry"]);
  const [addins, setAddins] = useState(["Chewy Ice"]);

  const price = useMemo(() => {
    let p = 3.5;
    if (creamer !== "None") p += 0.75;
    p += syrups.length * 0.5;
    p += addins.length * 0.5;
    return p;
  }, [creamer, syrups, addins]);

  const toggle = (list, setList, item) =>
    setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);

  const chip = (active) =>
    `rounded-full border px-3 py-1.5 font-mono text-xs transition-colors ${
      active
        ? "border-neon-pink bg-neon-pink/15 text-neon-pink"
        : "border-white/15 text-slate-400 hover:border-neon-pink/50 hover:text-slate-200"
    }`;

  return (
    <div className="glass relative overflow-hidden rounded-xl p-6 sm:p-8" data-testid="soda-builder">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-neon-pink/15 blur-3xl" />
      <div className="mb-6 flex items-center gap-3">
        <CupSoda className="h-6 w-6 text-neon-pink" />
        <div>
          <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-white">Build Your Dirty Soda</h3>
          <p className="text-xs text-slate-400">Mix it here, then order at the bar — we'll have it chilling.</p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-cyan">1 · Soda Base · $3.50</p>
          <div className="flex flex-wrap gap-2">
            {BASES.map((b) => (
              <button key={b} data-testid={`soda-base-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setBase(b)} className={chip(base === b)}>{b}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-cyan">2 · Creamer · +$0.75</p>
          <div className="flex flex-wrap gap-2">
            {CREAMERS.map((c) => (
              <button key={c} data-testid={`soda-creamer-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCreamer(c)} className={chip(creamer === c)}>{c}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-cyan">3 · Fruit Syrups · +$0.50 each</p>
          <div className="flex flex-wrap gap-2">
            {SYRUPS.map((s) => (
              <button key={s} data-testid={`soda-syrup-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => toggle(syrups, setSyrups, s)} className={chip(syrups.includes(s))}>
                {syrups.includes(s) && <Check className="mr-1 inline h-3 w-3" />}{s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-cyan">4 · Mix-ins · +$0.50 each</p>
          <div className="flex flex-wrap gap-2">
            {ADDINS.map((a) => (
              <button key={a} data-testid={`soda-addin-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => toggle(addins, setAddins, a)} className={chip(addins.includes(a))}>
                {addins.includes(a) && <Check className="mr-1 inline h-3 w-3" />}{a}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
        <div className="font-mono text-sm text-slate-300" data-testid="soda-builder-recipe">
          <Sparkles className="mr-1 inline h-4 w-4 text-neon-gold" />
          {base}{creamer !== "None" ? ` + ${creamer}` : ""}{syrups.length ? ` + ${syrups.join(", ")}` : ""}{addins.length ? ` + ${addins.join(", ")}` : ""}
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-2xl font-bold text-neon-gold" data-testid="soda-builder-price">${price.toFixed(2)}</span>
          <button data-testid="soda-builder-add-to-cart-button"
            onClick={() => toast.success("Recipe sent to the soda bar! Show this screen at the counter to skip the line.")}
            className="inline-flex items-center gap-2 rounded-md bg-neon-pink px-5 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-magenta">
            <Plus className="h-4 w-4" /> Send to Bar
          </button>
        </div>
      </div>
    </div>
  );
}
